import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShieldCheck, Zap } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';

/**
 * Source A: clone a CIVIX Agency Master package instantly (no manager approval).
 * @returns {JSX.Element}
 */
export default function MasterSyncPanel() {
  const qc = useQueryClient();
  const [packageId, setPackageId] = useState('');

  const { data } = useQuery({ queryKey: ['sync-packages'], queryFn: () => apiGet('/sync/packages'), staleTime: 5 * 60 * 1000 });
  const packages = data?.packages || [];
  const selected = packages.find((p) => p.id === packageId);

  // Pre-select the default bundle once the list loads.
  useEffect(() => {
    if (!packageId && packages.length) setPackageId(data?.defaultPackageId || packages[0].id);
  }, [packages, packageId, data]);

  const clone = useMutation({
    mutationFn: () => apiPost('/sync/master', { packageId }),
    onSuccess: (res) => {
      toast.success(res?.message || 'Master package cloned to your account', { icon: '🎉' });
      qc.invalidateQueries({ queryKey: ['sync-status'] });
      qc.invalidateQueries({ queryKey: ['sync-history'] });
    },
    onError: (err) => toast.error(err?.response?.data?.error || err.message || 'Master sync failed'),
  });

  return (
    <div className="space-y-4" data-testid="master-sync-panel">
      <div className="space-y-1">
        <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-civix-purpleSoft" /> Clone a CIVIX Agency Master Account
        </h2>
        <p className="text-white/60 text-xs">Start with a proven, production-ready setup. Nothing to wait for.</p>
      </div>

      <div className="space-y-2">
        <label htmlFor="master-package" className="block text-xs uppercase tracking-wider text-white/50 font-semibold">Agency Master Accounts</label>
        <select
          id="master-package"
          value={packageId}
          onChange={(e) => setPackageId(e.target.value)}
          className="w-full bg-[#130b20] border border-white/15 rounded-xl px-4 py-3 text-sm text-white font-medium focus:border-civix-cyan focus:outline-hidden transition cursor-pointer"
        >
          {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
          <Zap className="w-3 h-3" /> Instant Activation — No Approval Required
        </span>
      </div>

      {selected && (
        <div className="p-3.5 rounded-xl border border-white/10 bg-black/30 text-xs text-white/70 leading-relaxed" data-testid="master-summary">
          <div className="text-white font-semibold text-sm mb-1">{selected.name}</div>
          {selected.description} Includes turnkey workflows tailored for immediate production.
        </div>
      )}

      <button
        type="button"
        onClick={() => clone.mutate()}
        disabled={!packageId || clone.isPending}
        className="w-full py-3.5 px-6 rounded-xl font-bold text-sm bg-civix-gradient hover:opacity-95 text-white shadow-lg shadow-civix-purple/30 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 cursor-pointer"
      >
        <Zap className="w-4 h-4" />
        {clone.isPending ? 'Cloning…' : 'Clone Master Package (Instant)'}
      </button>
    </div>
  );
}
